import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Upload, X, Award } from "lucide-react";
import { useTranslation } from "react-i18next";

interface BadgeImageUploadProps {
  currentUrl?: string | null;
  onUploadComplete: (url: string) => void;
  onRemove: () => void;
}

export const BadgeImageUpload = ({
  currentUrl,
  onUploadComplete,
  onRemove,
}: BadgeImageUploadProps) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - only PNG
    if (file.type !== "image/png") {
      toast.error(t("admin.badges.upload.invalidFormat"));
      return;
    }

    // Validate file size (max 1MB)
    if (file.size > 1 * 1024 * 1024) {
      toast.error(t("admin.badges.upload.tooLarge"));
      return;
    }

    setUploading(true);

    try {
      const fileName = `badge-${Date.now()}.png`;

      const { error: uploadError } = await supabase.storage
        .from("badges")
        .upload(fileName, file, {
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("badges")
        .getPublicUrl(fileName);

      onUploadComplete(data.publicUrl);
      toast.success(t("admin.badges.upload.success"));
    } catch (error) {
      console.error("Error uploading badge image:", error);
      toast.error(t("admin.badges.upload.error"));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!currentUrl) return;

    try {
      const fileName = currentUrl.split("/").pop();
      if (fileName) {
        await supabase.storage.from("badges").remove([fileName]);
      }
      onRemove();
      toast.success(t("admin.badges.upload.removed"));
    } catch (error) {
      console.error("Error removing badge image:", error);
      toast.error(t("admin.badges.upload.removeError"));
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-base font-bold">{t("admin.badges.upload.label")}</Label>
      <p className="text-xs text-muted-foreground">
        {t("admin.badges.upload.description")}
      </p>

      {currentUrl ? (
        <div className="flex flex-col items-center gap-3">
          <div className="w-24 h-24 border-[3px] border-dark bg-surface flex items-center justify-center overflow-hidden">
            <img
              src={currentUrl}
              alt="Badge"
              className="w-full h-full object-contain p-2"
            />
          </div>
          <BrutalButton
            type="button"
            variant="danger"
            size="sm"
            onClick={handleRemove}
          >
            <X className="h-4 w-4 mr-2" />
            {t("admin.badges.upload.remove")}
          </BrutalButton>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="w-24 h-24 border-[3px] border-dashed border-muted-foreground bg-surface flex items-center justify-center">
            <Award className="w-8 h-8 text-muted-foreground" />
          </div>
          <div className="relative">
            <input
              type="file"
              accept="image/png"
              onChange={handleFileChange}
              disabled={uploading}
              className="hidden"
              id="badge-image-upload"
            />
            <label htmlFor="badge-image-upload" className="cursor-pointer">
              <div className="px-4 py-2 border-[3px] border-dark bg-card hover:bg-surface transition-colors font-heading font-bold flex items-center gap-2">
                <Upload className="h-4 w-4" />
                {uploading ? t("admin.badges.upload.uploading") : t("admin.badges.upload.button")}
              </div>
            </label>
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground text-center">
        {t("admin.badges.upload.format")}
      </p>
    </div>
  );
};